/**
 * Premium sticky header — real height reduction on scroll.
 *
 * Horizon's header section is `position: sticky`, so it stays IN FLOW and reserves its full
 * height at the top of the document even while stuck. Shrinking it would shrink that reserved
 * slot, the document would get shorter, and every pixel of content below would jump upward
 * mid-scroll.
 *
 * Rather than convert the header to `position: fixed` (which would break Horizon's own sticky
 * machinery — `header.js` detects stickiness with an IntersectionObserver at `threshold: 1` on
 * the header itself, and a fixed header never stops intersecting), we keep it sticky and
 * compensate the flow directly:
 *
 *     visible height (H - delta)  +  margin-block-end (delta)  =  H, always.
 *
 * The header's painted box shrinks; the space it reserves does not. Content below never moves.
 * The compensating margin sits at the header's flow position — the top of the document, which
 * the shopper has already scrolled past — so it is never visible.
 *
 * `delta` is measured rather than assumed, because the header's height depends on how many
 * rows the merchant enabled, the logo size, the height preset and the padding scale. Anything
 * hardcoded here would be wrong for most stores.
 *
 * FAILS OPEN: the CSS shrink is gated on `data-premium-header="ready"`, which is only set once
 * the observer is wired. If this script never runs, the header simply does not shrink and the
 * theme behaves exactly as it does today. The shrink can never happen without the measurement
 * that compensates for it.
 */

const READY_ATTR = 'data-premium-header';
const OFFSET_PROP = '--premium-header-offset';

/** The header group's flow footprint while expanded. Written here, read by assets/premium.css. */
const NATURAL_GROUP_PROP = '--premium-header-group-natural';

/** @returns {boolean} */
function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function init() {
  const header = /** @type {HTMLElement | null} */ (document.querySelector('#header-component'));
  if (!header) return;

  // Only merchants who asked for a sticky header get a shrinking one.
  if (!header.hasAttribute('sticky')) return;

  // A transparent header overlays the hero and swaps its colors off Horizon's own
  // `data-sticky-state`. Leave that path completely alone.
  if (header.hasAttribute('transparent')) return;

  const section = header.closest('.header-section');
  if (!(section instanceof HTMLElement)) return;

  if (!('ResizeObserver' in window)) return;

  const group = document.querySelector('#header-group');

  /** The header's height when it is NOT condensed. Re-measured whenever it is expanded. */
  let naturalHeight = 0;

  const measure = () => {
    const height = header.getBoundingClientRect().height;
    if (!height) return;

    if (header.dataset.stickyState !== 'active') {
      // While expanded, this IS the natural height — so a viewport resize, a font swap or a menu
      // reflow all keep it current without any extra listeners.
      naturalHeight = height;

      // PIN THE FLOW FOOTPRINT.
      //
      // `--header-group-height` is how much vertical room the header group occupies, and
      // full-height sections subtract it from their own height. The hero is literally
      // `min-height: calc(var(--hero-min-height) - var(--header-group-height))`
      // (sections/hero.liquid).
      //
      // header.js RECOMPUTES that value once the header goes sticky — it drops from
      // "announcement + header" down to just the condensed header. The hero grows by the whole
      // difference and shoves every section below it down the page, reintroducing exactly the
      // shift the margin compensation just removed from the header itself.
      //
      // We do not fight header.js for ownership of `--header-group-height` — it wins that race,
      // and racing over a shared property is not a fix. Instead we publish our own value, which
      // nothing else writes, and point the hero at it from premium.css.
      //
      // `--header-height` is deliberately NOT pinned: the mega menu clips itself against the
      // header's REAL height, and freezing that would open a gap beneath the condensed bar.
      //
      // The footprint is MEASURED rather than read back from `--header-group-height`. That
      // property is seeded coarsely by an inline script at parse time and only refined later by
      // header.js on its own schedule, so reading it means racing that schedule and pinning
      // whatever value happens to be there. `#header-group` is `display: contents`, so its
      // footprint is simply the sum of its children — the announcement bar plus the header.
      if (group) {
        let footprint = 0;
        for (const child of group.children) footprint += child.getBoundingClientRect().height;
        if (footprint) document.body.style.setProperty(NATURAL_GROUP_PROP, `${Math.round(footprint)}px`);
      }
    }

    const delta = Math.max(0, Math.round(naturalHeight - height));
    section.style.setProperty(OFFSET_PROP, `${delta}px`);
  };

  const observer = new ResizeObserver(measure);
  observer.observe(header);

  // The announcement bar is part of the header group's footprint but sits OUTSIDE the header, so
  // it can resize (a font swap, a text reflow) without the header ever changing size. Watching it
  // too keeps the pinned footprint honest.
  if (group instanceof Element) observer.observe(group);

  // header.js seeds `--header-group-height` from an inline script at parse time and then refines
  // it from its own ResizeObserver. Re-measuring on `load` — once fonts and images have settled —
  // is what stops us pinning the coarse, pre-layout value.
  window.addEventListener('load', measure, { once: true });

  // Unlocks the CSS condense. Set last, so the compensation is already live before the header is
  // ever allowed to change size.
  document.documentElement.setAttribute(READY_ATTR, 'ready');
}

// Reduced motion: no condense, no observer, no attribute — the header stays as Horizon ships it.
if (!prefersReducedMotion()) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
}
